/**
 * Canonical views registry — DEC-0409 step 2 (docs/specs/universal-instance-contract.md §4).
 *
 * One place answers "which views does this instance open with, in what order".
 * Before this module the answer lived in three copies that had to agree by hand:
 *   LENS_META in UniversalInstanceShell (labels + order of every key),
 *   WAVE1_LENSES in entity-instance-adapter (entity page tabs),
 *   APP_LENSES in app-home-adapter (App Home drawers).
 *
 * Pure and deterministic: no network, no database. The step-1 instance resolver
 * returns views from here, so every lens that resolves the same inputs gets the
 * same tab row.
 *
 * Storage: a type may declare its own ordered set in
 * entity_types.template_config.instance_views (jsonb — no schema change). No row
 * carries that key today, so every type resolves to the home default and the
 * screen is unchanged. Unknown keys are dropped, duplicates collapse, and
 * 'overview' is always kept first because the shell opens on it.
 */

import type { HomeType, WorkbenchLensKey } from '@/lib/crm/instance-contract';
import type { EntityType } from './types';

export interface InstanceViewDefinition {
  key: WorkbenchLensKey;
  label: { he: string; en: string };
}

/** Every instance view that exists, in catalog order. Labels are the shell's, verbatim. */
export const INSTANCE_VIEW_CATALOG: readonly InstanceViewDefinition[] = [
  { key: 'overview', label: { he: 'סקירה', en: 'Overview' } },
  { key: 'document', label: { he: 'מסמך', en: 'Document' } },
  { key: 'vtool', label: { he: 'כלי חזותי', en: 'vTool' } },
  { key: 'relations', label: { he: 'קשרים', en: 'Relations' } },
  { key: 'pm', label: { he: 'משימות', en: 'PM' } },
  { key: 'activity', label: { he: 'פעילות', en: 'Activity' } },
  { key: 'files', label: { he: 'קבצים', en: 'Files' } },
  { key: 'vcollection', label: { he: 'אוסף', en: 'vCollection' } },
  // [HS-1] Home drawers (REF-home-model §3).
  { key: 'spec', label: { he: 'איפיון חי', en: 'Living spec' } },
  { key: 'design', label: { he: 'עיצוב', en: 'Design' } },
  { key: 'versions', label: { he: 'גרסאות', en: 'Versions' } },
  { key: 'settings', label: { he: 'הגדרות', en: 'Settings' } },
];

const CATALOG_BY_KEY = new Map(INSTANCE_VIEW_CATALOG.map(v => [v.key, v]));

export function getInstanceViewDefinition(key: WorkbenchLensKey): InstanceViewDefinition | undefined {
  return CATALOG_BY_KEY.get(key);
}

/** Default view set per home. Entity and App are the two homes that render today. */
export const DEFAULT_INSTANCE_VIEWS: Readonly<Partial<Record<HomeType, readonly WorkbenchLensKey[]>>> = {
  entity: ['overview', 'document', 'vtool', 'relations', 'pm', 'activity', 'files', 'vcollection'],
  app: ['overview', 'spec', 'pm', 'relations', 'design', 'versions', 'settings', 'files'],
};

export type InstanceViewOrigin =
  /** The home's registry default. */
  | 'default'
  /** Declared by the entity type in template_config.instance_views. */
  | 'template';

export interface ResolvedInstanceView extends InstanceViewDefinition {
  origin: InstanceViewOrigin;
}

function isKnownKey(k: unknown): k is WorkbenchLensKey {
  return typeof k === 'string' && CATALOG_BY_KEY.has(k as WorkbenchLensKey);
}

/** Known keys only, first occurrence wins, 'overview' pinned first. Empty = nothing usable. */
export function normalizeInstanceViewKeys(raw: unknown): WorkbenchLensKey[] {
  if (!Array.isArray(raw)) return [];
  const keys = [...new Set(raw.filter(isKnownKey))];
  if (keys.length === 0) return [];
  return ['overview', ...keys.filter(k => k !== 'overview')];
}

/** Whether a type declares its own view set, or falls back to the home default. */
export function instanceViewOrigin(
  entityType: Pick<EntityType, 'template_config'> | null,
): InstanceViewOrigin {
  return normalizeInstanceViewKeys(
    (entityType?.template_config as { instance_views?: unknown } | null | undefined)?.instance_views,
  ).length > 0
    ? 'template'
    : 'default';
}

/** Catalog definitions for an already-resolved key list (DEC-0409 step 3 reuses this). */
export function describeInstanceViewKeys(
  keys: readonly WorkbenchLensKey[],
  origin: InstanceViewOrigin,
): ResolvedInstanceView[] {
  return keys.filter(k => CATALOG_BY_KEY.has(k)).map(key => ({ ...CATALOG_BY_KEY.get(key)!, origin }));
}

export function resolveInstanceViews(
  entityType: Pick<EntityType, 'template_config'> | null,
  homeType: HomeType = 'entity',
): ResolvedInstanceView[] {
  const declared = normalizeInstanceViewKeys(
    (entityType?.template_config as { instance_views?: unknown } | null | undefined)?.instance_views,
  );
  const keys = declared.length > 0 ? declared : (DEFAULT_INSTANCE_VIEWS[homeType] ?? DEFAULT_INSTANCE_VIEWS.entity!);
  return describeInstanceViewKeys(keys, declared.length > 0 ? 'template' : 'default');
}

/** The ordered key list the shell's tab row consumes. */
export function resolveInstanceViewKeys(
  entityType: Pick<EntityType, 'template_config'> | null,
  homeType: HomeType = 'entity',
): WorkbenchLensKey[] {
  return resolveInstanceViews(entityType, homeType).map(v => v.key);
}
